import type { TimeSeriesDataPoint, TimeSeriesSeries } from "./TimeSeries"

type Series = TimeSeriesSeries<string[]>

export interface AssetOption {
  /** Asset id (falls back to the series name when the series carries no asset id). */
  value: string
  label: string
  /** Names of every series that belongs to this asset (several when split by connection). */
  seriesNames: Set<string>
}

const optionKey = (s: Series): string => s.assetId ?? String(s.name)

/**
 * One option per asset across the given series lists, in first-seen order.
 * Passing several lists (battery discharge / charge / SoC) merges them, so an
 * asset present in any of them is offered once.
 */
export function buildAssetOptions(seriesLists: Series[][]): AssetOption[] {
  const byKey = new Map<string, Series[]>()
  for (const list of seriesLists) {
    for (const s of list) {
      const key = optionKey(s)
      const group = byKey.get(key)
      if (group) group.push(s)
      else byKey.set(key, [s])
    }
  }
  return Array.from(byKey.entries()).map(([value, group]) => {
    const names = new Set(group.map((s) => String(s.name)))
    // A lone series keeps its own label; several (connections / charge+discharge)
    // are labelled by the asset so the option does not read as one connection.
    const label =
      names.size === 1
        ? group[0].label
        : (group.find((s) => s.assetName)?.assetName ?? group[0].label)
    return { value, label, seriesNames: names }
  })
}

/** The selected option, or the first when the selection is unset or no longer present. */
export function resolveSelectedOption(
  options: AssetOption[],
  selected: string | null
): AssetOption | undefined {
  return options.find((o) => o.value === selected) ?? options[0]
}

export function filterSeriesToAsset(series: Series[], assetValue: string): Series[] {
  return series.filter((s) => optionKey(s) === assetValue)
}

/** Keep the timestamp plus the selected series' values (and their `_peak` companions). */
export function pruneDataToSeries(
  data: TimeSeriesDataPoint[],
  seriesNames: Set<string>
): TimeSeriesDataPoint[] {
  return data.map((row) => {
    const next = {} as TimeSeriesDataPoint
    for (const key of Object.keys(row)) {
      const isPeak = key.endsWith("_peak") && seriesNames.has(key.slice(0, -"_peak".length))
      if (key === "timestamp" || key === "time" || seriesNames.has(key) || isPeak) {
        next[key] = row[key]
      }
    }
    return next
  })
}
